import React from 'react'
import { Document, Page, Text, View, StyleSheet, Font } from '@react-pdf/renderer'

// Register fonts for better Turkish character support
Font.register({
  family: 'Roboto',
  fonts: [
    {
      src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-light-webfont.ttf',
      fontWeight: 300,
    },
    {
      src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-regular-webfont.ttf',
      fontWeight: 400,
    },
    {
      src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-medium-webfont.ttf',
      fontWeight: 500,
    },
    {
      src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-bold-webfont.ttf',
      fontWeight: 700,
    },
  ],
})

const colors = {
  primary: '#000000ff',
  success: '#28a745',
  danger: '#dc3545',
  warning: '#ffc107',
  light: '#f8f9fa',
  dark: '#343a40',
  gray: '#6c757d',
  border: '#dee2e6',
  background: '#ffffff',
}

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: colors.background,
    padding: 20,
    fontFamily: 'Roboto',
    fontSize: 10,
    lineHeight: 1.3,
  },

  // Header styles
  title: {
    fontSize: 16,
    fontWeight: 700,
    textAlign: 'center',
    marginBottom: 20,
    color: colors.primary,
    letterSpacing: 0.5,
  },

  // Info table styles
  infoTable: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 4, // Reduced from 6
    marginBottom: 20, // Reduced from 25
    overflow: 'hidden',
  },

  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },

  tableRowLast: {
    flexDirection: 'row',
  },

  tableCell: {
    padding: 8, // Reduced from 12
    flex: 1,
    backgroundColor: colors.light,
  },

  tableCellBordered: {
    borderRightWidth: 1,
    borderRightColor: colors.border,
  },

  infoLabel: {
    fontWeight: 700,
    color: colors.gray,
    marginBottom: 2, // Reduced from 4
    fontSize: 8, // Reduced from 9
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },

  infoValue: {
    fontWeight: 500,
    color: colors.dark,
    fontSize: 10,
  },

  // Score summary styles
  scoresContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 15,
    padding: 10,
    backgroundColor: colors.light,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: colors.border,
  },

  scoreItem: {
    alignItems: 'center',
  },

  scoreValue: {
    fontSize: 14,
    fontWeight: 700,
    marginBottom: 2,
  },

  scoreLabel: {
    fontSize: 8,
    color: colors.gray,
    textTransform: 'uppercase',
  },

  correctScore: {
    color: colors.success,
  },

  incorrectScore: {
    color: colors.danger,
  },

  emptyScore: {
    color: colors.gray,
  },

  totalScore: {
    color: colors.primary,
  },

  // Section titles
  sectionTitle: {
    fontSize: 12,
    fontWeight: 700,
    marginBottom: 10,
    marginTop: 15,
    color: colors.dark,
    borderBottomWidth: 1,
    borderBottomColor: colors.primary,
    paddingBottom: 3,
  },

  // Question styles
  questionContainer: {
    marginBottom: 8,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 4,
    backgroundColor: colors.background,
    overflow: 'hidden',
  },

  questionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 8,
    backgroundColor: colors.light,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },

  questionTitle: {
    flex: 1,
    fontWeight: 600,
    fontSize: 10,
    color: colors.dark,
    marginRight: 8,
    lineHeight: 1.2,
  },

  statusBadge: {
    width: 55,
    fontSize: 7,
    fontWeight: 700,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 10,
    textAlign: 'center',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },

  correctBadge: {
    backgroundColor: colors.success,
    color: colors.background,
  },

  incorrectBadge: {
    backgroundColor: colors.danger,
    color: colors.background,
  },

  emptyBadge: {
    backgroundColor: colors.gray,
    color: colors.background,
  },

  // Answer choices styles
  answersSection: {
    padding: 8,
  },

  choicesLabel: {
    fontWeight: 600,
    marginBottom: 4,
    color: colors.dark,
    fontSize: 9,
  },

  choiceItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 3,
    paddingVertical: 1,
  },

  choicePrefix: {
    width: 12,
    fontSize: 10,
    fontWeight: 700,
    marginRight: 6,
  },

  choiceText: {
    flex: 1,
    fontSize: 9,
    lineHeight: 1.2,
  },

  correctChoice: {
    color: colors.success,
  },

  incorrectChoice: {
    color: colors.danger,
  },

  neutralChoice: {
    color: colors.dark,
  },

  emptyAnswer: {
    fontStyle: 'italic',
    color: colors.gray,
    textAlign: 'center',
    padding: 6, // Reduced from 10
    fontSize: 9,
  },
})

interface ExamResultPDFProps {
  result: any
  allQuestions: any[]
}

const ExamResultPDF: React.FC<ExamResultPDFProps> = ({ result, allQuestions }) => {
  const correctAnswers = result.responses.filter((r: any) => r.isCorrect === true).length
  const wrongAnswers = result.responses.filter((r: any) => r.isCorrect === false).length
  const answeredQuestions = correctAnswers + wrongAnswers
  const total_questions = result.totalQuestions || 30
  const emptyAnswers = total_questions - answeredQuestions
  const isCategoriesEmpty = Object.keys(result.categories || {}).length === 0
  const score = isCategoriesEmpty
    ? result.score || correctAnswers
    : result.weightedScore || result.score || correctAnswers
  const time_limit = result.timeLimit || 0

  const startTime = new Date(result.startedAt)
  let endTime, durationMs, durationMinutes, durationSeconds, endTimeDisplay

  if (result.completedAt) {
    endTime = new Date(result.completedAt)
    durationMs = endTime.getTime() - startTime.getTime()
    durationMinutes = Math.floor(durationMs / 60000)
    durationSeconds = Math.floor((durationMs % 60000) / 1000)
    endTimeDisplay = `${endTime.toLocaleDateString('tr-TR')} ${endTime.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}`
  } else {
    durationMinutes = 0
    durationSeconds = 0
    endTimeDisplay = '-'
  }

  const stripHtml = (html) => {
    if (!html) return ''
    return html
      .replace(/<[^>]*>?/gm, '') // Remove HTML tags
      .replace(/&nbsp;/g, ' ') // Replace non-breaking spaces
      .replace(/&amp;/g, '&') // Replace ampersands
      .replace(/&lt;/g, '<') // Replace less than
      .replace(/&gt;/g, '>') // Replace greater than
  }

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Title */}
        <Text style={styles.title}>KULLANICI SINAV SONUÇ RAPORU</Text>

        {/* Info Table */}
        <View style={styles.infoTable}>
          <View style={styles.tableRow}>
            <View style={[styles.tableCell, styles.tableCellBordered]}>
              <Text style={styles.infoLabel}>Çalışan / Sicil</Text>
              <Text style={styles.infoValue}>
                {result.candidateName} / {result.sicil || 'N/A'}
              </Text>
            </View>
            <View style={styles.tableCell}>
              <Text style={styles.infoLabel}>Mağaza</Text>
              <Text style={styles.infoValue}>{result.magaza || 'Belirtilmemiş'}</Text>
            </View>
          </View>

          <View style={styles.tableRow}>
            <View style={[styles.tableCell, styles.tableCellBordered]}>
              <Text style={styles.infoLabel}>Puan</Text>
              <Text style={styles.infoValue}>{score}</Text>
            </View>
            <View style={styles.tableCell}>
              <Text style={styles.infoLabel}>Sınav Süresi</Text>
              <Text style={styles.infoValue}>
                {result.completedAt ? `${durationMinutes} dk ${durationSeconds} sn` : '-'}
              </Text>
            </View>
          </View>

          <View style={styles.tableRow}>
            <View style={[styles.tableCell, styles.tableCellBordered]}>
              <Text style={styles.infoLabel}>Başlama Zamanı</Text>
              <Text style={styles.infoValue}>
                {startTime.toLocaleDateString('tr-TR')}{' '}
                {startTime.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
              </Text>
            </View>
            <View style={styles.tableCell}>
              <Text style={styles.infoLabel}>Bitiş Zamanı</Text>
              <Text style={styles.infoValue}>{endTimeDisplay}</Text>
            </View>
          </View>

          <View style={styles.tableRowLast}>
            <View style={[styles.tableCell, styles.tableCellBordered]}>
              <Text style={styles.infoLabel}>Toplam Soru</Text>
              <Text style={styles.infoValue}>{total_questions}</Text>
            </View>
            <View style={styles.tableCell}>
              <Text style={styles.infoLabel}>Süre Limiti</Text>
              <Text style={styles.infoValue}>{time_limit / 60} dakika</Text>
            </View>
          </View>
        </View>

        {/* Score Summary */}
        <View style={styles.scoresContainer}>
          <View style={styles.scoreItem}>
            <Text style={[styles.scoreValue, styles.correctScore]}>{correctAnswers}</Text>
            <Text style={styles.scoreLabel}>Doğru</Text>
          </View>
          <View style={styles.scoreItem}>
            <Text style={[styles.scoreValue, styles.incorrectScore]}>{wrongAnswers}</Text>
            <Text style={styles.scoreLabel}>Yanlış</Text>
          </View>
          <View style={styles.scoreItem}>
            <Text style={[styles.scoreValue, styles.emptyScore]}>{emptyAnswers}</Text>
            <Text style={styles.scoreLabel}>Boş</Text>
          </View>
        </View>

        {/* Questions Section */}
        <Text style={styles.sectionTitle}>SORULAR VE CEVAPLARI</Text>

        {allQuestions.map((response, index) => {
          const questionNumber = index + 1

          let statusStyle, statusText
          if (response.isCorrect === true) {
            statusStyle = styles.correctBadge
            statusText = 'DOĞRU'
          } else if (response.isCorrect === false) {
            statusStyle = styles.incorrectBadge
            statusText = 'YANLIŞ'
          } else {
            statusStyle = styles.emptyBadge
            statusText = 'BOŞ'
          }

          return (
            <View key={response.questionId} style={styles.questionContainer} wrap={false}>
              <View style={styles.questionHeader}>
                <Text style={styles.questionTitle}>
                  S{questionNumber}: {stripHtml(response.questionText) || 'Soru metni bulunamadı'}
                </Text>
                <Text style={[styles.statusBadge, statusStyle]}>{statusText}</Text>
              </View>

              <View style={styles.answersSection}>
                {response.allChoices && response.allChoices.length > 0 ? (
                  response.allChoices.map((choice, choiceIndex) => {
                    let textStyle = styles.neutralChoice

                    if (choice.isCorrect) {
                      textStyle = styles.correctChoice
                    } else if (choice.isSelected) {
                      textStyle = styles.incorrectChoice
                    }

                    return (
                      <View key={choice.id} style={styles.choiceItem}>
                        <Text style={[styles.choiceText, textStyle]}>
                          {String.fromCharCode(65 + choiceIndex)}) {choice.text}
                        </Text>
                      </View>
                    )
                  })
                ) : (
                  <Text style={styles.emptyAnswer}>Seçenek bilgisi bulunamadı</Text>
                )}

                {!response.choiceId &&
                  (!response.allChoices || response.allChoices.length === 0) && (
                    <Text style={styles.emptyAnswer}>Bu soru boş bırakılmış</Text>
                  )}
              </View>
            </View>
          )
        })}
      </Page>
    </Document>
  )
}

export default ExamResultPDF
